import { useEffect, useState } from 'react';
import { api, type CarryKind, type CarryPreview } from '../lib/api';
import { ErrorBox } from './DataTable';

/**
 * 편성 이어받기(게시판 #65) — 종료일을 넣은 편성에 **종료 뒤** 남은 월정기·할부·재무추가를
 * 같은 차량의 새 편성으로 한 번에 옮긴다. 종료일이 없으면 아무것도 안 보인다(예비기사 교대는 원래 편성이 맞다 — 서버 머리말).
 * 기본 = 전부 옮기기 체크 · 후보 편성이 하나면 그것으로(고객에게 고르게 하지 않는다).
 */
export default function AllocCarryPanel({ carAllocId, readOnly, reloadKey }: {
  carAllocId: number; readOnly: boolean; reloadKey: number;
}) {
  const [p, setP] = useState<CarryPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [kinds, setKinds] = useState<Record<CarryKind, boolean>>({ mo: true, inst: true, daily: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const r = await api.previewCarAllocCarry({ carAllocId });
      setP(r);
      if (r.ended) setTo((cur) => (cur && r.targets.some((t) => t.car_alloc_id === cur) ? cur : r.targets[0]?.car_alloc_id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => { void load(); }, [carAllocId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorBox message={error} />;
  if (!p || !p.ended) return null;
  const total = p.moDefs + p.moRows + p.inst + p.daily;
  if (total === 0) return done ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{done}</p> : null;

  const rows: { k: CarryKind; label: string; n: number; show: boolean }[] = [
    { k: 'mo', label: `월정기 정기항목 ${p.moDefs}개${p.moRows ? ` (이미 등록된 달 ${p.moRows}줄 포함)` : ''}`, n: p.moDefs + p.moRows, show: p.moDefs + p.moRows > 0 },
    { k: 'inst', label: `할부 남은 회차 ${p.inst}회`, n: p.inst, show: p.inst > 0 },
    { k: 'daily', label: `재무추가 ${p.daily}건`, n: p.daily, show: p.daily > 0 },
  ];
  const picked = rows.filter((r) => r.show && kinds[r.k]).map((r) => r.k);

  async function carry() {
    if (!to || !picked.length) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.carryCarAlloc({ fromId: carAllocId, toId: to, kinds: picked });
      const parts = [
        r.moDefs || r.moRows ? `월정기 ${r.moDefs}개(달 줄 ${r.moRows})` : '',
        r.inst ? `할부 ${r.inst}회` : '',
        r.daily ? `재무추가 ${r.daily}건` : '',
      ].filter(Boolean);
      setDone(`${to}번 편성으로 옮겼습니다 — ${parts.join(' · ') || '옮길 것 없음'}.`
        + (r.denied.length ? ` ${r.denied.join('·')}은(는) 입력 권한이 없어 옮기지 않았습니다.` : ''));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      <p>
        <b>이 편성은 {p.enddt}에 끝났습니다.</b> {p.from}부터 이 편성에 남아 있는 항목이 있습니다
        <span className="text-amber-800"> ({p.months.join(', ')})</span>. 같은 차량의 새 편성으로 옮기세요.
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {rows.filter((r) => r.show).map((r) => (
          <label key={r.k} className="inline-flex items-center gap-1.5">
            <input type="checkbox" checked={kinds[r.k]} disabled={readOnly || busy}
              onChange={(e) => setKinds((cur) => ({ ...cur, [r.k]: e.target.checked }))} />
            {r.label}
          </label>
        ))}
      </div>
      {p.targets.length === 0 ? (
        <p className="text-amber-800">옮길 곳이 없습니다 — 같은 차량의 새 편성(종료일 없음)을 먼저 등록하세요.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span>옮길 편성</span>
          <select className="field w-auto" value={to ?? ''} disabled={readOnly || busy} onChange={(e) => setTo(Number(e.target.value))}>
            {p.targets.map((t) => (
              <option key={t.car_alloc_id} value={t.car_alloc_id}>
                {t.car_alloc_id}번 · {t.driver_name ?? '기사 없음'} · {t.owner_name ?? '-'} · {t.grp_name ?? '-'}{t.enddt ? ` · ~${t.enddt}` : ''}
              </option>
            ))}
          </select>
          {!readOnly && (
            <button className="btn-primary" onClick={carry} disabled={busy || !to || picked.length === 0}>
              {busy ? '옮기는 중…' : '새 편성으로 옮기기'}
            </button>
          )}
        </div>
      )}
      <p className="text-xs text-amber-800">
        종료일이 든 달({p.enddt.slice(0, 7)})과 마감된 달은 옮기지 않습니다. 배차는 종료일 뒤로 잡혀 있으면 저장 단계에서 이미 막힙니다.
      </p>
      {done && <p className="text-emerald-800">{done}</p>}
    </div>
  );
}
